import type { Campaign, SignagePlace } from './api'

// The Campaigns week: each day's blocks at one branch, by the rule a screen plays by
// (internal/signage Running and Playing): a high campaign plays alone, equal ones share.

export interface Block {
  campaign: Campaign
  /** Minutes from the day's midnight, branch clock; to is at most 1440. */
  from: number
  to: number
  high: boolean
  /** Its playlist holds nothing, so a screen leaves it out. */
  empty: boolean
  /** Side-by-side place among the blocks it overlaps. */
  lane: number
  lanes: number
  /** Stretches a high campaign takes over (normal blocks only). */
  outranked: Array<[number, number]>
  /** Another campaign of its priority plays at the same time; they take turns. */
  shared: boolean
}

export interface CalendarDay {
  date: string
  /** ISO weekday, Monday 1. */
  weekday: number
  blocks: Block[]
}

const DAY_MS = 86_400_000

const asUTC = (day: string) => Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)))
const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10)

/** The day n days after day (YYYY-MM-DD). */
export function addDays(day: string, n: number): string {
  return isoDay(asUTC(day) + n * DAY_MS)
}

/** The ISO weekday of a calendar day, Monday 1. */
export function weekdayOf(day: string): number {
  return ((new Date(asUTC(day)).getUTCDay() + 6) % 7) + 1
}

/** The Monday of day's week. */
export function weekStartOf(day: string): string {
  return addDays(day, 1 - weekdayOf(day))
}

/** Whether a campaign plays on a day at a branch's place, ignoring its hours. */
export function playsOn(c: Campaign, day: string, branchId: string, place: SignagePlace): boolean {
  return (
    c.active &&
    c.startDate <= day &&
    day <= c.endDate &&
    (c.weekdays.length === 0 || c.weekdays.includes(weekdayOf(day))) &&
    c.branchIds.includes(branchId) &&
    c.places.includes(place)
  )
}

/** Its hours as minutes; all day is the whole day. */
export function spanOf(c: Pick<Campaign, 'fromMin' | 'toMin'>): [number, number] {
  return c.fromMin === 0 && c.toMin === 0 ? [0, 1440] : [c.fromMin, Math.min(c.toMin, 1440)]
}

function overlap(a: [number, number], b: [number, number]): [number, number] | null {
  const from = Math.max(a[0], b[0])
  const to = Math.min(a[1], b[1])
  return from < to ? [from, to] : null
}

/** Ranges joined where they touch or overlap, in order. */
export function merged(ranges: ReadonlyArray<[number, number]>): Array<[number, number]> {
  const out: Array<[number, number]> = []
  for (const r of [...ranges].sort((a, b) => a[0] - b[0])) {
    const last = out[out.length - 1]
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1])
    else out.push([r[0], r[1]])
  }
  return out
}

/** One day's blocks: lanes for overlaps, the stretches a high one takes, and shared time. */
export function dayBlocks(campaigns: readonly Campaign[], day: string, branchId: string, place: SignagePlace, hasItems: (playlistId: string) => boolean): Block[] {
  const blocks: Block[] = campaigns
    .filter((c) => playsOn(c, day, branchId, place))
    .map((c) => {
      const [from, to] = spanOf(c)
      return { campaign: c, from, to, high: c.priority === 'high', empty: !hasItems(c.playlistId), lane: 0, lanes: 1, outranked: [], shared: false }
    })
    .filter((b) => b.from < b.to)
    .sort((a, b) => a.from - b.from || b.to - a.to || a.campaign.name.localeCompare(b.campaign.name))

  const playing = blocks.filter((b) => !b.empty)
  for (const b of playing) {
    const mine: [number, number] = [b.from, b.to]
    if (!b.high) {
      b.outranked = merged(
        playing.filter((o) => o.high).map((o) => overlap(mine, [o.from, o.to])).filter((r): r is [number, number] => r !== null),
      )
    }
    b.shared = playing.some((o) => o !== b && o.high === b.high && overlap(mine, [o.from, o.to]) !== null)
  }

  // Greedy lanes within each run of overlapping blocks.
  let run: Block[] = []
  let runEnd = -1
  const close = () => {
    const lanes = run.reduce((n, b) => Math.max(n, b.lane + 1), 1)
    for (const b of run) b.lanes = lanes
    run = []
  }
  for (const b of blocks) {
    if (run.length > 0 && b.from >= runEnd) close()
    const ends: number[] = []
    for (const o of run) ends[o.lane] = Math.max(ends[o.lane] ?? 0, o.to)
    let lane = 0
    while (ends[lane] !== undefined && ends[lane]! > b.from) lane++
    b.lane = lane
    run.push(b)
    runEnd = Math.max(runEnd, b.to)
  }
  if (run.length > 0) close()
  return blocks
}

/** The seven days from monday at a branch's place. */
export function weekOf(campaigns: readonly Campaign[], monday: string, branchId: string, place: SignagePlace, hasItems: (playlistId: string) => boolean): CalendarDay[] {
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(monday, i)
    return { date, weekday: i + 1, blocks: dayBlocks(campaigns, date, branchId, place, hasItems) }
  })
}

/** The hours the grid shows: 06 to 22, widened to hold every timed block. */
export function hourRange(days: readonly CalendarDay[]): [number, number] {
  let from = 6
  let to = 22
  for (const d of days) {
    for (const b of d.blocks) {
      if (b.from === 0 && b.to === 1440) continue
      from = Math.min(from, Math.floor(b.from / 60))
      to = Math.max(to, Math.ceil(b.to / 60))
    }
  }
  return [from, to]
}

/** A campaign's colour slot, 1 to 8, by its place in the list so it stays put between weeks. */
export function colourOf(campaigns: readonly Pick<Campaign, 'id'>[], id: string): number {
  const i = campaigns.findIndex((c) => c.id === id)
  return (Math.max(i, 0) % 8) + 1
}

/** Minutes as HH:MM; 1440 is 24:00. */
export function clockOfMinutes(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
}
